import type { HouseholdItem, HouseholdStatus, MonthSummary } from './types';

export function deriveStatus(
  dueAt: string,
  explicit?: string | null,
  now = new Date()
): HouseholdStatus {
  const normalized = (explicit || '').toLowerCase();
  if (['done', 'paid', 'completed', 'handled'].includes(normalized)) {
    return 'done';
  }
  if (['overdue', 'failed', 'needs_attention', 'attention'].includes(normalized)) {
    return 'needs_attention';
  }

  const due = new Date(dueAt);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntil = Math.ceil((due.getTime() - startOfToday.getTime()) / msPerDay);

  if (daysUntil < 0) return 'needs_attention';
  if (daysUntil <= 7) return 'needs_attention';
  return 'upcoming';
}

export function summarizeMonth(
  items: HouseholdItem[],
  reference = new Date()
): MonthSummary {
  const month = reference.getMonth();
  const year = reference.getFullYear();
  const inMonth = items.filter((item) => {
    const d = new Date(item.dueAt);
    return d.getMonth() === month && d.getFullYear() === year;
  });

  return {
    monthLabel: reference.toLocaleString('en-AU', { month: 'long' }),
    bills: inMonth.filter((i) => i.category === 'bill').length,
    renewals: inMonth.filter((i) => i.category === 'renewal').length,
    events: inMonth.filter((i) => i.category === 'event').length,
    needsAttention: inMonth.filter((i) => i.status === 'needs_attention').length,
  };
}

export function itemsForRange(
  items: HouseholdItem[],
  start: Date,
  end: Date
): HouseholdItem[] {
  return items.filter((item) => {
    const d = new Date(item.dueAt);
    return d >= start && d <= end;
  });
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

export function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  return startOfDay(addDays(d, diff));
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), q, 1);
}

export function endOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3) * 3 + 2;
  return new Date(d.getFullYear(), q + 1, 0, 23, 59, 59, 999);
}

export function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

export function endOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
}

export function formatCurrency(amount?: number, currency = 'AUD'): string {
  if (amount == null) return '';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDue(dueAt: string): string {
  return new Date(dueAt).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
  });
}
